import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import type { ButtonHTMLAttributes } from 'react';

const variants = {
  primary:
    'bg-brand text-white shadow-xs hover:bg-brand-hover active:bg-brand-active',
  secondary:
    'bg-bg-surface border border-border-strong text-text-primary shadow-xs hover:bg-bg-subtle hover:border-border-strong active:bg-bg-subtle',
  ghost:
    'bg-transparent text-text-secondary hover:bg-bg-subtle hover:text-text-primary',
  destructive:
    'bg-danger text-white shadow-xs hover:bg-danger/90 active:bg-danger',
  'destructive-outline':
    'bg-bg-surface border border-danger/30 text-danger hover:bg-danger-subtle',
};

const sizes = {
  sm: 'h-8 px-3 text-[13px] gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-5 text-[15px] gap-2 rounded-lg',
  icon: 'h-9 w-9 rounded-lg',
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: keyof typeof variants;
  size?: keyof typeof sizes;
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      className={cn(
        'inline-flex cursor-pointer select-none items-center justify-center whitespace-nowrap font-medium transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-app',
        'disabled:cursor-not-allowed disabled:pointer-events-none disabled:opacity-50',
        sizes[size],
        variants[variant],
        className,
      )}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}
